import { changeHTMLAttribute } from '@/utils/layout';
import { createContext, useContext, useEffect, useState } from 'react';

const LayoutContext = createContext(undefined);

function useLayoutContext() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayoutContext must be used within an LayoutProvider');
  }
  return context;
}

const themeKey = 'data-bs-theme';

function readSavedTheme() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return 'light';
  }
  const foundTheme = localStorage.getItem(themeKey);
  if (foundTheme === 'auto') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  if (foundTheme === 'dark' || foundTheme === 'light') return foundTheme;
  return 'light';
}

const LayoutProvider = ({
  children
}) => {
  const [settings, setSettings] = useState({
    theme: 'light',
    dir: 'ltr'
  });

  useEffect(() => {
    const theme = readSavedTheme();
    if (!localStorage.getItem(themeKey)) {
      localStorage.setItem(themeKey, theme);
    }
    changeHTMLAttribute(themeKey, theme);
    setSettings((prev) => (prev.theme === theme ? prev : { ...prev, theme }));
  }, []);

  const updateSettings = _newSettings => setSettings({
    ...settings,
    ..._newSettings
  });
  const updateTheme = newTheme => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    const foundTheme = localStorage.getItem(themeKey);
    if (foundTheme !== newTheme) {
      changeHTMLAttribute(themeKey, newTheme);
      localStorage.setItem(themeKey, newTheme);
      updateSettings({
        ...settings,
        theme: newTheme
      });
    }
  };
  const updateDir = newDir => updateSettings({
    ...settings,
    dir: newDir
  });
  return <LayoutContext.Provider value={{
    ...settings,
    updateTheme,
    updateDir
  }}>
      {children}
    </LayoutContext.Provider>;
};
export { useLayoutContext, LayoutProvider };
